import type { Architecture, Channel, Platform, RuntimeComponent, RuntimeComponentInput } from "@player115/contracts";
import { ExternalLink, Plus, RefreshCw, Save } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { api, demoMode } from "../api";
import { PageState, Status } from "../components/Status";
import "./ReleasesPage.css";

const blankComponent: RuntimeComponentInput = {
  componentKey: "ai_subtitles",
  displayName: "AI 字幕运行时",
  platform: "windows",
  architecture: "x86_64",
  channel: "stable",
  version: "1.0.0",
  downloadUrl: "",
  sha256: "",
  sizeBytes: 51274234,
  enabled: false,
};

function toInput(component: RuntimeComponent): RuntimeComponentInput {
  const { id: _id, createdAt: _createdAt, updatedAt: _updatedAt, ...input } = component;
  return input;
}

function platformName(platform: Platform) {
  return platform === "macos" ? "macOS" : platform === "windows" ? "Windows" : platform === "android-tv" ? "Android TV" : platform === "android-mobile" ? "Android Mobile" : "Linux";
}

function formatBytes(bytes: number) {
  return `${(bytes / 1024 / 1024).toFixed(2)} MiB`;
}

export function RuntimeComponentsPage() {
  const [components, setComponents] = useState<RuntimeComponent[]>([]);
  const [draft, setDraft] = useState<RuntimeComponentInput>(blankComponent);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const load = async () => {
    try {
      const result = await api.runtimeComponents();
      setComponents(result.components);
      if (result.components[0] && !selectedId) {
        setSelectedId(result.components[0].id);
        setDraft(toInput(result.components[0]));
      }
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "组件配置读取失败");
    }
  };
  useEffect(() => { void load(); }, []);
  const selected = useMemo(() => components.find((item) => item.id === selectedId), [components, selectedId]);
  const update = <K extends keyof RuntimeComponentInput>(key: K, value: RuntimeComponentInput[K]) => {
    setDraft((current) => ({ ...current, [key]: value }));
    setMessage("");
  };
  const choose = (component: RuntimeComponent) => {
    setSelectedId(component.id);
    setDraft(toInput(component));
    setMessage("");
    setError("");
  };
  const createNew = () => {
    setSelectedId(null);
    setDraft(blankComponent);
    setMessage("");
    setError("");
  };
  const save = async () => {
    setBusy(true);
    setMessage("");
    setError("");
    try {
      const result = await api.saveRuntimeComponent(draft);
      setComponents((current) => [...current.filter((item) => item.id !== result.component.id && !(item.componentKey === result.component.componentKey && item.platform === result.component.platform && item.architecture === result.component.architecture && item.channel === result.component.channel)), result.component].sort((a, b) => `${a.componentKey}${a.platform}${a.architecture}${a.channel}`.localeCompare(`${b.componentKey}${b.platform}${b.architecture}${b.channel}`)));
      setSelectedId(result.component.id);
      setDraft(toInput(result.component));
      setMessage("组件配置已保存并写入审计日志");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "组件配置保存失败");
    } finally {
      setBusy(false);
    }
  };

  return <section className="page releases-page">
    <header className="page-head"><div><h1>组件资源</h1><p>管理客户端按需下载的独立运行时</p></div><div className="page-head-actions"><Status tone={demoMode ? "warning" : "success"}>{demoMode ? "演示数据" : "远端策略"}</Status><button className="primary-button" type="button" onClick={createNew}><Plus size={17} />新建配置</button></div></header>
    {error && <div className="inline-error" role="alert"><span>{error}</span><button type="button" onClick={() => void load()}><RefreshCw size={15} />重试</button></div>}
    <div className="release-targets">
      <header className="release-targets-head"><div><strong>组件目标</strong><small>选择要编辑的平台运行时配置</small></div><span>{components.length} 个目标</span></header>
      {components.length ? <div className="release-target-list" aria-label="组件资源目标">
        {components.map((component) => <button className={component.id === selectedId ? "is-selected" : ""} aria-pressed={component.id === selectedId} type="button" key={component.id} onClick={() => choose(component)}><span className="release-target-copy"><strong>{component.displayName}</strong><small>{platformName(component.platform)} · {component.architecture} · {component.channel}</small></span>{component.enabled ? <Status tone="success">v{component.version}</Status> : <Status tone="muted">停用</Status>}</button>)}
      </div> : <PageState title="暂无组件配置" detail="创建第一个按需下载组件。" />}
    </div>
    <form className="release-editor" onSubmit={(event) => { event.preventDefault(); void save(); }}>
        <header><div><h2>{selected ? `编辑 ${selected.displayName}` : "新建组件配置"}</h2><p>{draft.platform} / {draft.architecture} / {draft.channel}</p></div>{draft.enabled ? <Status tone="success">已启用</Status> : <Status tone="muted">未启用</Status>}</header>
        <section className="form-section"><h3>组件标识</h3><div className="form-grid two">
          <label><span>组件键</span><input className="mono" value={draft.componentKey} onChange={(event) => update("componentKey", event.target.value)} placeholder="ai_subtitles" /></label>
          <label><span>显示名称</span><input value={draft.displayName} onChange={(event) => update("displayName", event.target.value)} /></label>
        </div></section>
        <section className="form-section"><h3>下发目标</h3><div className="form-grid three">
          <label><span>平台</span><select value={draft.platform} onChange={(event) => update("platform", event.target.value as Platform)}><option value="windows">Windows</option><option value="macos">macOS</option><option value="android-tv">Android TV</option><option value="android-mobile">Android Mobile</option><option value="linux">Linux</option></select></label>
          <label><span>架构</span><select value={draft.architecture} onChange={(event) => update("architecture", event.target.value as Architecture)}><option value="x86_64">x86_64</option><option value="aarch64">ARM64</option><option value="armv7">ARMv7（32 位）</option></select></label>
          <label><span>渠道</span><select value={draft.channel} onChange={(event) => update("channel", event.target.value as Channel)}><option value="stable">稳定版</option><option value="beta">测试版</option></select></label>
        </div></section>
        <section className="form-section"><h3>资源信息</h3><div className="form-grid two">
          <label><span>组件版本</span><input value={draft.version} onChange={(event) => update("version", event.target.value)} placeholder="1.0.0" /></label>
          <label><span>文件大小（字节）</span><input className="mono" type="number" min={1} max={1073741824} value={draft.sizeBytes} onChange={(event) => update("sizeBytes", Number(event.target.value))} /><small>{draft.sizeBytes > 0 ? formatBytes(draft.sizeBytes) : "请输入实际文件大小"}</small></label>
          <label className="span-two"><span>ZIP 直链</span><div className="input-action"><input type="url" value={draft.downloadUrl} onChange={(event) => update("downloadUrl", event.target.value)} placeholder="https://.../runtime.zip" />{draft.downloadUrl && <a href={draft.downloadUrl} target="_blank" rel="noreferrer" title="打开下载地址"><ExternalLink size={17} /></a>}</div></label>
          <label className="span-two"><span>SHA-256</span><input className="mono" value={draft.sha256} onChange={(event) => update("sha256", event.target.value.toUpperCase())} placeholder="64 位十六进制" /></label>
        </div></section>
        <section className="publish-row"><label><input type="checkbox" checked={draft.enabled} onChange={(event) => update("enabled", event.target.checked)} /><span>启用并下发此组件</span></label><div>{message && <Status tone="success">{message}</Status>}<button className="primary-button" type="submit" disabled={busy || !draft.downloadUrl || draft.sha256.length !== 64 || draft.sizeBytes <= 0}>{busy ? <RefreshCw className="spin" size={17} /> : <Save size={17} />}{busy ? "保存中" : "保存配置"}</button></div></section>
    </form>
  </section>;
}
